// Navbar. Black bar, yellow accent, sharp edges. Sticky.
import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "@/data/config";

const NAV = [
  { to: "/portfolio", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/brand-branding", label: "Insights" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-black text-white border-b border-white/10"
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 lg:gap-3 group -ml-1"
          aria-label="Kostaja home"
        >
          <img
            src="/brand/kostaja-favicon.png"
            alt=""
            aria-hidden="true"
            data-testid="nav-logo-symbol"
            className="h-7 w-7 lg:h-9 lg:w-9 shrink-0 select-none object-contain transition-opacity group-hover:opacity-80"
            draggable="false"
          />
          <span
            data-testid="nav-logo-wordmark"
            className="text-xl lg:text-[26px] font-black tracking-[-0.02em] text-white group-hover:text-[#FFC600] transition-colors leading-none"
            style={{ fontFamily: "Poppins, ui-sans-serif, system-ui, sans-serif" }}
          >
            KOSTAJA
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `k-link text-sm font-medium ${isActive ? "text-[#FFC600]" : "text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/waitlist"
            data-testid="nav-apply"
            className="inline-flex items-center justify-center bg-[#FFC600] text-black text-sm font-semibold px-5 py-2.5 border-2 border-[#FFC600] hover:bg-black hover:text-[#FFC600] transition-colors"
          >
            Apply to Work With Kostaja
          </Link>
        </div>

        <button
          type="button"
          data-testid="nav-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 border-2 border-white hover:border-[#FFC600] hover:text-[#FFC600]"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t-2 border-[#FFC600] bg-black"
        >
          <div className="px-5 py-5 grid grid-cols-1 gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block py-3 text-base border-b border-white/20 ${
                    isActive ? "text-[#FFC600] font-semibold" : "text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/waitlist"
              data-testid="mobile-nav-apply"
              className="mt-4 inline-flex items-center justify-center bg-[#FFC600] text-black font-semibold px-5 py-3 border-2 border-[#FFC600]"
            >
              Apply to Work With Kostaja
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
